import React, { useState, useEffect, useMemo } from 'react';

export function LiveRoundTimer({ openMiningRound, currentRound }) {
  const [now, setNow] = useState(Date.now());

  // Update every second for live countdown
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  const roundData = useMemo(() => {
    if (!openMiningRound) return null;

    const opensAt = new Date(openMiningRound.opensAt).getTime();
    const targetClosesAt = new Date(openMiningRound.targetClosesAt).getTime();
    const duration = targetClosesAt - opensAt;
    const elapsed = now - opensAt;
    const remaining = Math.max(0, targetClosesAt - now);
    const progress = Math.min(100, Math.max(0, (elapsed / duration) * 100));

    // Format remaining time
    const totalSeconds = Math.floor(remaining / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return {
      roundNumber: openMiningRound.round?.number || currentRound,
      amuletPrice: parseFloat(openMiningRound.amuletPrice),
      opensAt,
      targetClosesAt,
      remaining,
      progress,
      timeDisplay: `${minutes}:${seconds.toString().padStart(2, '0')}`,
      isClosing: remaining < 60000, // Less than 1 minute
      issuanceConfig: openMiningRound.issuanceConfig,
    };
  }, [openMiningRound, currentRound, now]);

  if (!roundData) {
    return null;
  }

  return (
    <div style={{
      background: 'var(--bg-elevated)',
      border: '1px solid var(--border-default)',
      borderRadius: '8px',
      padding: '20px 24px',
      marginBottom: '24px',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Progress bar background */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        height: '100%',
        width: `${roundData.progress}%`,
        background: 'rgba(255, 255, 255, 0.02)',
        transition: 'width 1s linear',
      }} />

      <div style={{ position: 'relative', zIndex: 1 }}>
        {/* Header row */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '16px',
          flexWrap: 'wrap',
          gap: '12px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: 'var(--accent)',
              boxShadow: '0 0 8px rgba(0, 255, 136, 0.5)',
              animation: 'pulse 2s ease-in-out infinite',
            }} />
            <span style={{
              fontSize: '11px',
              color: 'var(--text-tertiary)',
              textTransform: 'uppercase',
              letterSpacing: '1.5px',
            }}>Live Round</span>
          </div>
          <div style={{
            fontFamily: 'var(--font-mono)',
            fontSize: '11px',
            color: 'var(--text-disabled)',
          }}>
            {Math.round(roundData.progress)}% complete
          </div>
        </div>

        {/* Main content */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
          gap: '20px',
          alignItems: 'center',
        }}>
          {/* Round Number */}
          <div>
            <div style={{
              fontSize: '10px',
              color: 'var(--text-disabled)',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              marginBottom: '4px',
            }}>Round</div>
            <div style={{
              fontSize: '24px',
              fontWeight: '700',
              color: 'var(--text-primary)',
              fontFamily: 'var(--font-mono)',
              lineHeight: 1,
            }}>
              #{parseInt(roundData.roundNumber).toLocaleString()}
            </div>
          </div>

          {/* Countdown */}
          <div>
            <div style={{
              fontSize: '10px',
              color: 'var(--text-disabled)',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              marginBottom: '4px',
            }}>Closes In</div>
            <div style={{
              fontSize: '24px',
              fontWeight: '700',
              color: roundData.isClosing ? 'var(--text-secondary)' : 'var(--text-primary)',
              fontFamily: 'var(--font-mono)',
              lineHeight: 1,
            }}>
              {roundData.timeDisplay}
            </div>
          </div>

          {/* CC Price */}
          <div>
            <div style={{
              fontSize: '10px',
              color: 'var(--text-disabled)',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              marginBottom: '4px',
            }}>CC Price</div>
            <div style={{
              fontSize: '20px',
              fontWeight: '700',
              color: 'var(--text-secondary)',
              fontFamily: 'var(--font-mono)',
              lineHeight: 1,
            }}>
              ${roundData.amuletPrice.toFixed(4)}
            </div>
          </div>

          {/* Validator Reward */}
          <div className="hide-mobile">
            <div style={{
              fontSize: '10px',
              color: 'var(--text-disabled)',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              marginBottom: '4px',
            }}>Validator Cap</div>
            <div style={{
              fontSize: '18px',
              fontWeight: '600',
              color: 'var(--text-tertiary)',
              fontFamily: 'var(--font-mono)',
              lineHeight: 1,
            }}>
              {roundData.issuanceConfig?.validatorRewardCap || '0.2'} CC
            </div>
          </div>

          {/* Faucet Cap */}
          <div className="hide-mobile">
            <div style={{
              fontSize: '10px',
              color: 'var(--text-disabled)',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              marginBottom: '4px',
            }}>Faucet Cap</div>
            <div style={{
              fontSize: '18px',
              fontWeight: '600',
              color: 'var(--text-tertiary)',
              fontFamily: 'var(--font-mono)',
              lineHeight: 1,
            }}>
              {parseFloat(roundData.issuanceConfig?.optValidatorFaucetCap || 570).toFixed(0)} CC
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div style={{
          marginTop: '16px',
          height: '2px',
          background: 'var(--border-subtle)',
          borderRadius: '1px',
          overflow: 'hidden',
        }}>
          <div style={{
            height: '100%',
            width: `${roundData.progress}%`,
            background: 'var(--accent)',
            boxShadow: '0 0 6px rgba(0, 255, 136, 0.4)',
            borderRadius: '1px',
            transition: 'width 1s linear',
          }} />
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </div>
  );
}
